// The trash: bookmarks deleted in the last 30 days, which can be restored
// with everything they had, or deleted for good now. Laid out like the
// highlights view (its toolbar and container classes), with each bookmark as
// a card.
import { api } from './store.ts';
import { el, formatDate, hostname } from '../../ui/shared/dom.ts';
import type { Bookmark } from '../../ui/shared/types.ts';

/** Look up an element the page is expected to contain (see highlights.ts). */
function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

let items: Bookmark[] = [];
/** Called after a restore, so the bookmark list can pick it up. */
let onRestored: () => void = () => {};

function daysLeft(purgeAt: string | undefined): string {
  if (!purgeAt) return '';
  const days = Math.max(0, Math.ceil((Date.parse(purgeAt) - Date.now()) / 86_400_000));
  return days <= 1 ? 'deleted for good within a day' : `deleted for good in ${days} days`;
}

function renderCard(bookmark: Bookmark): HTMLElement {
  return el('div', { class: 'annotation-card trash-card', dataset: { id: String(bookmark.id) } }, [
    el('div', { class: 'highlight-source' },
      `${hostname(bookmark.url)} · deleted ${formatDate(bookmark.deleted_at)} · ${daysLeft(bookmark.purge_at)}`),
    el('div', { class: 'trash-title' }, bookmark.title || bookmark.url),
    bookmark.tags.length > 0 && el('div', { class: 'highlight-source' }, bookmark.tags.map((t) => `#${t}`).join(' ')),
    el('div', { class: 'annotation-actions' }, [
      el('button', { class: 'btn-sm primary', type: 'button', onclick: () => restore(bookmark) }, 'restore'),
      el('button', { class: 'btn-sm danger', type: 'button', onclick: () => deleteForever(bookmark) },
        'delete forever'),
    ]),
  ]);
}

function render(): void {
  const list = $('trash-list');
  list.replaceChildren(...(items.length
    ? items.map(renderCard)
    : [el('div', { class: 'msg' }, 'The trash is empty. Deleted bookmarks stay here for 30 days.')]));
  $<HTMLButtonElement>('trash-empty').disabled = !items.length;
}

async function restore(bookmark: Bookmark): Promise<void> {
  try {
    await api.restoreBookmark(bookmark.id);
    items = items.filter((b) => b.id !== bookmark.id);
    render();
    onRestored();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function deleteForever(bookmark: Bookmark): Promise<void> {
  if (!confirm(`Delete "${bookmark.title || bookmark.url}" for good? Its article and highlights go with it.`)) return;
  try {
    await api.deleteForever(bookmark.id);
    items = items.filter((b) => b.id !== bookmark.id);
    render();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function emptyTrash(): Promise<void> {
  if (!items.length) return;
  if (!confirm(`Delete all ${items.length} bookmark${items.length === 1 ? '' : 's'} in the trash for good?`)) return;
  try {
    await api.emptyTrash();
    items = [];
    render();
  } catch (err) {
    alert((err as Error).message);
  }
}

export async function openTrashView(restored: () => void): Promise<void> {
  onRestored = restored;
  $('trash-list').replaceChildren(el('div', { class: 'msg' }, 'loading...'));
  try {
    items = await api.getTrash();
    render();
  } catch (err) {
    $('trash-list').replaceChildren(el('div', { class: 'reader-error' }, (err as Error).message));
  }
}

export function closeTrashView(): void {
  items = [];
}

$('trash-empty').addEventListener('click', emptyTrash);
$('trash-back').addEventListener('click', () => { window.location.hash = ''; });
